import React, { useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Stack,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate } from 'react-router-dom';
import { useCoordinateContext } from '~/hooks/coordinate/useCoordinateContext';
import { CoordinationStatus, CoordinatePatient, STATUS_CHIP_STYLE } from '~/types/coordinate';

const STATUS_LABELS: Record<CoordinationStatus, string> = {
  pending: 'Pending',
  matching: 'Matching',
  matched: 'Destination Identified',
  confirming: 'Confirming',
  confirmed: 'Ready',
  confirm_failed: 'Needs Attention',
  awaiting_response: 'Awaiting Response',
  negotiating: 'Negotiating',
  accepted: 'Accepted',
  declined: 'Declined',
};

const STATUS_COLORS: Record<CoordinationStatus, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'> = {
  pending: 'default',
  matching: 'info',
  matched: 'secondary',
  confirming: 'info',
  confirmed: 'success',
  confirm_failed: 'warning',
  awaiting_response: 'info',
  negotiating: 'secondary',
  accepted: 'success',
  declined: 'warning',
};

const KPICard = ({ label, value, sub, icon }: { label: string; value: string; sub?: string; icon: React.ReactNode }) => (
  <Paper sx={{ p: 2.5 }}>
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {label.toUpperCase()}
        </Typography>
        <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5 }}>
          {value}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.secondary">{sub}</Typography>
        )}
      </Box>
      <Box sx={{ color: 'primary.main', opacity: 0.6, mt: 0.5 }}>{icon}</Box>
    </Stack>
  </Paper>
);

// Signal type for per-patient AI work in flight
interface AiSignal {
  patient: CoordinatePatient;
  icon: React.ReactNode;
  label: string;
  color: string;
  bgcolor: string;
}

function buildAiSignals(patients: CoordinatePatient[]): AiSignal[] {
  const signals: AiSignal[] = [];
  patients.forEach(p => {
    const pending = (p.agentDrafts ?? []).filter(d => d.status === 'pending').length;
    if (pending > 0) {
      signals.push({
        patient: p,
        icon: <AutoAwesomeIcon sx={{ fontSize: 13 }} />,
        label: `${pending} draft${pending > 1 ? 's' : ''} pending your review`,
        color: '#635f99',
        bgcolor: '#f0eef8',
      });
    }
    if (p.providerOutreach?.attempts.some(a => a.status === 'calling')) {
      signals.push({
        patient: p,
        icon: <PhoneInTalkIcon sx={{ fontSize: 13 }} />,
        label: 'Outreach calls in progress',
        color: '#1565c0',
        bgcolor: '#e3f2fd',
      });
    }
    if (p.insuranceTrack?.status === 'issue') {
      signals.push({
        patient: p,
        icon: <WarningAmberIcon sx={{ fontSize: 13 }} />,
        label: 'Insurance issue — needs attention',
        color: '#e65100',
        bgcolor: '#fff3e0',
      });
    }
    if (p.insuranceTrack?.status === 'pending') {
      signals.push({
        patient: p,
        icon: <WarningAmberIcon sx={{ fontSize: 13 }} />,
        label: 'Insurance auth pending',
        color: '#1565c0',
        bgcolor: '#e3f2fd',
      });
    }
  });
  return signals;
}

const CoordinateKPIs: React.FC = () => {
  const { patients } = useCoordinateContext();
  const navigate = useNavigate();

  const sentCount = patients.filter(p =>
    ['awaiting_response', 'accepted', 'declined'].includes(p.coordinationStatus)
  ).length;

  const acceptedCount = patients.filter(p => p.coordinationStatus === 'accepted').length;
  const totalResponded = patients.filter(p =>
    p.coordinationStatus === 'accepted' || p.coordinationStatus === 'declined'
  ).length;
  const acceptanceRate = totalResponded > 0
    ? Math.round((acceptedCount / totalResponded) * 100)
    : 84;

  const pendingAIDecisions = useMemo(
    () => patients.filter(p => (p.agentDrafts ?? []).some(d => d.status === 'pending')).length,
    [patients]
  );

  const aiSignals = useMemo(() => buildAiSignals(patients), [patients]);

  const statusCounts = patients.reduce<Partial<Record<CoordinationStatus, number>>>((acc, p) => {
    acc[p.coordinationStatus] = (acc[p.coordinationStatus] ?? 0) + 1;
    return acc;
  }, {});

  const orderedStatuses: CoordinationStatus[] = [
    'pending', 'matched', 'confirmed', 'awaiting_response', 'accepted', 'confirm_failed', 'declined',
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Coordinate Analytics</Typography>
        <Typography variant="body2" color="text.secondary">Post-acute referral orchestration overview</Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            label="Patients in Coordination"
            value={String(patients.length)}
            sub="across all statuses"
            icon={<CheckCircleIcon fontSize="large" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            label="Referrals Sent"
            value={String(sentCount)}
            sub="this session"
            icon={<SendIcon fontSize="large" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            label="Pending AI Decisions"
            value={String(pendingAIDecisions)}
            sub={pendingAIDecisions === 0 ? 'all drafts reviewed' : 'drafts awaiting review'}
            icon={<AutoAwesomeIcon fontSize="large" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            label="Acceptance Rate"
            value={`${acceptanceRate}%`}
            sub={totalResponded > 0 ? `${acceptedCount} of ${totalResponded} responded` : 'mock estimate'}
            icon={<ThumbUpIcon fontSize="large" />}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
              Patient Status Breakdown
            </Typography>
            <Stack spacing={1.5}>
              {orderedStatuses.map(status => {
                const count = statusCounts[status] ?? 0;
                if (count === 0) return null;
                const pct = Math.round((count / patients.length) * 100);
                return (
                  <Box
                    key={status}
                    onClick={() => navigate('/coordinate/patients', { state: { statusFilter: [status] } })}
                    sx={{ cursor: 'pointer', borderRadius: 1, px: 0.5, py: 0.25, mx: -0.5, '&:hover': { bgcolor: 'action.hover' }, transition: 'background-color 0.1s' }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Chip
                        label={STATUS_LABELS[status]}
                        size="small"
                        color={STATUS_COLORS[status]}
                        variant="outlined"
                      />
                      <Typography variant="body2" fontWeight={600}>{count}</Typography>
                    </Stack>
                    <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'grey.100', overflow: 'hidden' }}>
                      <Box
                        sx={{
                          height: '100%',
                          width: `${pct}%`,
                          bgcolor: `${STATUS_COLORS[status]}.main`,
                          borderRadius: 3,
                          transition: 'width 0.4s ease',
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2.5 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <AutoAwesomeIcon sx={{ fontSize: 16, color: '#635f99' }} />
              <Typography variant="subtitle1" fontWeight={700}>
                AI Work in Flight
              </Typography>
              {aiSignals.length > 0 && (
                <Chip
                  label={aiSignals.length}
                  size="small"
                  sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#f0eef8', color: '#635f99', fontWeight: 700 }}
                />
              )}
            </Stack>
            {aiSignals.length === 0 ? (
              <Typography variant="body2" color="text.disabled" sx={{ py: 2, textAlign: 'center' }}>
                No active AI work — all decisions reviewed
              </Typography>
            ) : (
              <List disablePadding>
                {aiSignals.map((sig, i) => (
                  <React.Fragment key={`${sig.patient.id}-${i}`}>
                    <ListItem
                      disablePadding
                      onClick={() => navigate(`/coordinate/patient/${sig.patient.id}`)}
                      sx={{ py: 1, cursor: 'pointer', borderRadius: 1, '&:hover': { bgcolor: 'action.hover' }, px: 0.5 }}
                    >
                      <Box sx={{ mr: 1.25, color: sig.color, bgcolor: sig.bgcolor, borderRadius: 1, p: 0.5, display: 'flex', flexShrink: 0 }}>
                        {sig.icon}
                      </Box>
                      <ListItemText
                        primary={
                          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                            <Typography variant="body2" fontWeight={600}>{sig.patient.name}</Typography>
                            <Chip
                              label={STATUS_CHIP_STYLE[sig.patient.coordinationStatus].label}
                              size="small"
                              sx={{
                                height: 18, fontSize: '0.6rem', fontWeight: 700,
                                bgcolor: STATUS_CHIP_STYLE[sig.patient.coordinationStatus].bgcolor,
                                color: STATUS_CHIP_STYLE[sig.patient.coordinationStatus].color,
                              }}
                            />
                          </Stack>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">{sig.label}</Typography>
                        }
                      />
                    </ListItem>
                    {i < aiSignals.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CoordinateKPIs;
